// Compétitions d'une équipe sur la saison (D-090) : par équipe engagée (SF1, SF2… ou tout le groupe),
// dans l'ordre où elles se jouent — brassage, championnat, phases finales —, coupes et amicaux à part.
// Fonctions pures (testées).
import type { CompetitionType } from './ffbb';

export interface SeasonLink {
  engagement_id: string; competition_name: string; competition_type: CompetitionType;
  poule_name?: string | null; squad_id?: string | null; team_number?: number | null; created_at?: string | null;
}
export interface SeasonSquad { id: string; name: string; position: number }
/** Une équipe engagée (null : tout le groupe), ses étapes de la saison et ce qui se joue à côté. */
export interface SeasonSection<L extends SeasonLink> { squad: SeasonSquad | null; steps: L[]; apart: L[] }

const STEP: Record<CompetitionType, number> = { brassage: 0, championnat: 1, phase_finale: 2, coupe: 3, amical: 4 };
const isApart = (l: SeasonLink) => l.competition_type === 'coupe' || l.competition_type === 'amical';

/** L'ordre de la saison : brassage → championnat → phases finales (coupe, amical ensuite), puis l'ordre de liaison. */
export const bySeason = (a: SeasonLink, b: SeasonLink): number =>
  STEP[a.competition_type] - STEP[b.competition_type] || (a.created_at ?? '').localeCompare(b.created_at ?? '');

/**
 * Les compétitions d'un groupe, par équipe engagée : « tout le groupe » d'abord (les brassages des
 * jeunes, joués avant les championnats), puis SF1, SF2… même sans compétition (à relier). Sans équipe
 * engagée : une seule section. Un lien vers une équipe engagée inconnue compte pour tout le groupe.
 */
export function seasonSections<L extends SeasonLink>(links: L[], squads: SeasonSquad[]): SeasonSection<L>[] {
  const sq = [...squads].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const ids = new Set(sq.map(q => q.id));
  const owner = (l: L) => (l.squad_id && ids.has(l.squad_id) ? l.squad_id : null);
  const section = (squad: SeasonSquad | null): SeasonSection<L> => {
    const mine = links.filter(l => owner(l) === (squad?.id ?? null)).sort(bySeason);
    return { squad, steps: mine.filter(l => !isApart(l)), apart: mine.filter(isApart) };
  };
  const group = section(null);
  if (!sq.length) return [group];
  return [...(group.steps.length || group.apart.length ? [group] : []), ...sq.map(section)];
}

/** « Joué par » se choisit quand la FFBB ne dit pas quelle équipe engagée joue (engagement sans numéro),
 *  ou que son numéro n'a trouvé aucune équipe engagée ; sinon il reste modifiable à la demande. */
export const playedByOpen = (l: SeasonLink, squadCount: number): boolean => squadCount > 0 && (l.team_number == null || !l.squad_id);

/** « Poule A », « Poule PYR-A » (la FFBB écrit l'un ou l'autre). */
export const pouleLabel = (name: string | null | undefined): string | null => {
  const n = (name ?? '').trim();
  return !n ? null : /^poule\b/i.test(n) ? n : `Poule ${n}`;
};
